import React, { useState } from 'react';
import { Box, Divider, Backdrop } from '@mui/material';
import { useForm, FormProvider, FieldErrors } from 'react-hook-form';
import Spinner from '../common/Spinner';
import ErrorAlert from '../common/ErrorAlert';
import InfoAlert from '../common/InfoAlert';
import { analyticsService } from '../../services/analyticsService';
import { RiskGroupDto } from '../../models/medicalCheckups/riskGroupDto';
import { CompanyShortDto } from '../../models/companies/companyShortDto';
import { RiskGroupFilterBar, RiskGroupFilterForm } from './RiskGroupFilterBar';
import { RiskGroupHeader } from './RiskGroupHeader';
import { RiskGroupDataTable } from './RiskGroupDataTable';

export const RiskGroup: React.FC = () => {
    const [data, setData] = useState<RiskGroupDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [info, setInfo] = useState<string | null>(null);

    const methods = useForm<RiskGroupFilterForm>({
        defaultValues: {
            fromDate: '',
            toDate: '',
            types: [],
            company: null,
            terminals: [],
            drivers: [],
            ageFrom: undefined,
            ageTo: undefined,
            rejectReasons: [],
        }
    });

    const onSubmit = (formData: RiskGroupFilterForm) => {
        setLoading(true);
        setError(null);
        analyticsService.getByFilter({
            fromDate: formData.fromDate,
            toDate: formData.toDate,
            types: formData.types,
            companyId: formData.company!.id,
            terminalIds: formData.terminals.map((t) => t.id),
            driverIds: formData.drivers.map((e) => e.id),
            ageFrom: formData.ageFrom === "" || formData.ageFrom === null ? undefined : formData.ageFrom as number,
            ageTo: formData.ageTo === "" || formData.ageTo === null ? undefined : formData.ageTo as number,
            rejectReasonIds: formData.rejectReasons.map((r) => r.id),
        })
            .then(r => {
                setData(r.data.data);
                if (r.data.data.drivers.length === 0) {
                    setInfo('Данные не найдены. Попробуйте изменить параметры поиска.');
                } else {
                    setInfo(null);
                }
            })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    };

    const onSubmitError = (errors: FieldErrors<RiskGroupFilterForm>) => {
        setError('Исправьте ошибки ввода');
    };

    const handleExport = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await analyticsService.exportToExcel(data!);
            const filename = getFilenameFromHeaders(response.headers['content-disposition']);
            const contentType = response.headers['content-type'];
            
            downloadFile(response.data, filename, contentType);
        } catch (e: any) {
            console.error('Export error:', e);
            setError(e.message || 'Ошибка экспорта');
        } finally {
            setLoading(false);
        }
    };

    const getFilenameFromHeaders = (contentDisposition: string | undefined): string => {
        const parts = contentDisposition?.split(';') || [];
        const filenamePart = parts.find(part => part.trim().startsWith('filename='));
        const filename = filenamePart?.replace('filename=', '').trim() || '';
        return decodeURIComponent(filename);
    };

    const downloadFile = (data: any, filename: string, contentType: string): void => {
        const blob = new Blob([data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const formFromDate = methods.watch('fromDate');
    const formToDate = methods.watch('toDate');
    const formCompany = methods.watch('company');

    return (
        <Box>
            <FormProvider {...methods}>
                <RiskGroupFilterBar
                    methods={methods}
                    onSubmit={onSubmit}
                    onSubmitError={onSubmitError}
                />
            </FormProvider>

            {error && (
                <ErrorAlert
                    message={error}
                    onClose={() => setError('')}
                />
            )}

            {info && (
                <InfoAlert
                    message={info}
                    severity="info"
                    onClose={() => setInfo('')}
                    autoCloseDuration={8000}
                />
            )}

            <Divider sx={{ my: 2, borderColor: '#e0e0e0', borderWidth: 1 }} />

            <RiskGroupHeader
                fromDate={formFromDate}
                toDate={formToDate}
                company={formCompany as CompanyShortDto | null}
                data={data}
                onExport={handleExport}
            />

            <RiskGroupDataTable data={data} />

            <Backdrop
                sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}
                open={loading}
            >
                <Spinner />
            </Backdrop>
        </Box>
    );
}; 